import { useEffect, useRef, useState } from 'react';
import { Box, Button, Column, Modal, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import HY_tooth from '../../images/HY_tooth.png';
import { UserSubProfession } from '../../enums/UserProfession';

// Profile image is circular — DS borderRadius.pill = 25px, not 50%.
const PROFILE_IMAGE_STYLE = {
  width: 100,
  height: 100,
  borderRadius: '50%',
  objectFit: 'cover' as const,
};

// Fallback avatar circle matches legacy .default_circle_container size.
const AVATAR_FALLBACK_STYLE = {
  width: 100,
  height: 100,
  borderRadius: '50%',
  backgroundColor: colors.neutral[100],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
} as const;

// Timezone badge dimensions — specific 33×16px pill shape with no DS token equivalent.
const TIMEZONE_BADGE_STYLE = {
  width: 33,
  height: 16,
  backgroundColor: colors.neutral[300],
  borderRadius: 100,
  marginLeft: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

// Dispute modal textarea — mirrors legacy sizing; no DS InputTextArea fits this exact layout.
const TEXTAREA_STYLE = {
  fontSize: 16,
  margin: '0 35px',
  width: 'calc(100% - 70px)',
  height: 192,
  borderRadius: 10,
  boxShadow: '0px 2px 4px 0px #00000040 inset',
  borderColor: 'white',
  backgroundColor: colors.neutral[50],
  color: colors.neutral[600],
} as const;

interface Job {
  offer_final_reciever?: {
    first_name?: string;
    last_name?: string;
    profile_image?: string | null;
  } | null;
  professionName?: string;
  subProfession?: string;
}

export const HygienistHeaderInfo = ({ job }: { job: Job }) => (
  <Column align="center" style={{ width: '100%', paddingTop: 10, marginBottom: 20 }}>
    <Row align="center" gap={5}>
      {job?.offer_final_reciever?.profile_image ? (
        <img
          src={job.offer_final_reciever.profile_image}
          style={PROFILE_IMAGE_STYLE}
          alt="profile_image"
        />
      ) : (
        <div style={AVATAR_FALLBACK_STYLE}>
          <img src={HY_tooth} alt="profile_image" width="50px" />
        </div>
      )}
      <Column>
        <Typography bold color={colors.neutral[600]}>
          {`${job?.offer_final_reciever?.first_name || ''} ${
            job?.offer_final_reciever?.last_name || ''
          }`}
        </Typography>
        <Typography color={colors.neutral[600]}>
          {UserSubProfession.getName(job?.subProfession ?? '') || job?.professionName || ''}
        </Typography>
      </Column>
    </Row>
  </Column>
);

interface RenderLineProps {
  title: string;
  value: string;
  isAdjustedField?: boolean;
  isOldFied?: boolean;
  timezone?: boolean;
}

export const RenderLine = ({ title, value, isAdjustedField, isOldFied, timezone }: RenderLineProps) => (
  <Box style={{ width: '100%' }}>
    <Row
      align="center"
      justify="between"
      py={3}
      px={8}
    >
      <Row align="center">
        <Typography
          bold
          color={isAdjustedField ? colors.primary[700] : colors.neutral[600]}
        >
          {title}
        </Typography>
        {timezone && (
          <div style={TIMEZONE_BADGE_STYLE}>
            <Typography size="label" bold color="white">
              EDT
            </Typography>
          </div>
        )}
      </Row>
      <Typography
        color={colors.neutral[600]}
        style={{ textDecoration: isOldFied ? 'line-through' : 'none' }}
      >
        {value}
      </Typography>
    </Row>
    <Box style={{ height: 1, backgroundColor: colors.neutral[100] }} />
  </Box>
);

interface DisputeReasonModalProps {
  closeModal: () => void;
  submitDisputeReasonMessage: (message: string) => void;
}

export const DisputeReasonModal = ({
  closeModal,
  submitDisputeReasonMessage,
}: DisputeReasonModalProps) => {
  const [disputeReasonMessage, setDisputeReasonMessage] = useState('');

  return (
    <Modal
      open
      onClose={closeModal}
      closeOnOverlayClick
      closeOnEscape
    >
      <Modal.Header>
        <Typography size="heading_3" bold textAlign="center" color={colors.neutral[600]}>
          Reason For Dispute
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <Column align="center" gap={4}>
          <Typography italic color={colors.neutral[500]} textAlign="center" style={{ maxWidth: 425 }}>
            Please explain in detail the reason for your dispute and the time you think is fair so
            we can best assist you. Upon submission, our Adjustment team will resolve this dispute
            within 24 hours and release payment accordingly.
          </Typography>
          <textarea
            value={disputeReasonMessage}
            placeholder="Reason for adjustment"
            onChange={(e) => setDisputeReasonMessage(e.target.value)}
            style={TEXTAREA_STYLE}
          />
        </Column>
      </Modal.Body>
      <Modal.Footer>
        <Row justify="center">
          <Button
            disabled={!disputeReasonMessage}
            onClick={() => submitDisputeReasonMessage(disputeReasonMessage)}
            size="sm"
          >
            Submit
          </Button>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};
